import type { RecentActivityDto } from "@gladis/shared";
import { EmptyState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { formatDateTime } from "@gladis/ui/i18n";

/**
 * ⑥ Who did what — five lines, and a link to the rest.
 *
 * ==========================================================================================
 * A LIST, NOT A `StatCard`
 * ==========================================================================================
 *
 * Research R14 settles it: `StatCard`'s own documentation says "everything in a stat card must be a
 * number or a name", and every line here is a sentence. `dashboard.md`'s marks table calls ⑥ and ⑦ "List"
 * and "One panel, five rows" — neither is a chart, so AC-D25 has no view on them, and neither needed a new
 * `@gladis/ui` surface.
 *
 * ==========================================================================================
 * THE "VIEW ALL" LINK EXISTS, AND RESEARCH R13 SAID IT WOULD NOT
 * ==========================================================================================
 *
 * That finding recorded the link as "conditional on the Activity Log page existing", with the condition
 * false at planning time — `/activity` rendered `<ComingSoon title={"Activity"} feature="012" />`, and R13
 * was explicit that the link "is added in 018's commit, not guarded at runtime here".
 *
 * 018 shipped. `/activity` is a real page with 018's own feed, its paging and its system-event toggle, so
 * the condition is now true and the link is here — a fact about the route table rather than a runtime
 * check, exactly as R13 framed it.
 *
 * ==========================================================================================
 * THE TIME IS ABSOLUTE, AND THAT DISAGREES WITH ⑦ ON PURPOSE
 * ==========================================================================================
 *
 * `ActivityRow` in the audit package refuses relative time and gives the reason: "the question the log
 * answers is 'when did this happen', not 'how long ago'". These five lines are the same kind of statement,
 * so they read the same way.
 *
 * ⑦ beside it is the opposite case — "how old is this module's data" IS a duration — which is why one
 * panel says "Updated 3 days ago" and this one says "11 Aug 09:14". The two are not an inconsistency to
 * reconcile.
 */

/**
 * The instant, in the reader's LANGUAGE — feature 021.
 *
 * It used to pass `undefined` to `toLocaleString`, which reads whatever the browser reports. That was
 * fine while the browser also decided the words; it stopped being fine the moment an explicit choice
 * could disagree with it, which is the same correction `ActivityRow` needed one package along.
 */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  return formatDateTime(at);
}

export function RecentActivityCard({ entries }: { entries: RecentActivityDto[] }): ReactNode {
  const t = useT();
  return (
    <section
      aria-labelledby="recent-activity-heading"
      data-testid="recent-activity-card"
      className="rounded-control border border-line bg-panel p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2
          id="recent-activity-heading"
          className="text-xs font-medium tracking-wide text-muted uppercase"
        >
          {t("dash.activity.heading")}
        </h2>
        {/* The link is present even when the list is empty: the feed holds the system events this card
            excludes, so there is something to see there either way. */}
        <Link to="/activity" className="text-xs text-muted underline hover:text-ink">
          {t("dash.activity.viewAll")}
        </Link>
      </div>

      {entries.length === 0 ? (
        /*
         * NOT HIDDEN (FR-073, AC-D18). A household that has recorded nothing, and one whose only recent
         * events were the scheduled price fetch, both land here — and in the second case the "view all"
         * link above is the answer to "then what happened?".
         */
        <div className="mt-3">
          <EmptyState>{t("dash.activity.empty")}</EmptyState>
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {entries.map((entry) => (
            <li key={entry.id} className="flex flex-col gap-0.5">
              {/*
                The message is the FINISHED SENTENCE, composed by the context that emitted the event and
                stored with it — it already names the actor and the figure, so this renders it rather than
                assembling anything from parts.
              */}
              <span className="text-sm text-ink">{entry.message}</span>
              <span className="text-xs text-muted">
                {/* The name that member had WHEN IT HAPPENED, snapshotted at write time (AC-AL3). */}
                {entry.actorDisplayName} · {formatWhen(entry.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
